import React, { useState, useCallback, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { LogOut, Settings, ChevronDown } from 'lucide-react';
import { AuthUser } from '../../services';
import UserAvatar from './UserAvatar';
import { dropdownVariants } from './animations';

interface UserDropdownMenuProps {
  /** Current user data */
  user: AuthUser | null;
  /** Logout handler */
  onLogout: () => void;
}

/**
 * UserDropdownMenu Component
 * 
 * User profile dropdown with menu options.
 * Features:
 * - Click outside to close
 * - Escape key to close
 * - Role-based menu items
 * - Accessible keyboard navigation
 * - Logout confirmation modal
 * 
 * @example
 * <UserDropdownMenu user={user} onLogout={handleLogout} />
 */
const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showLogoutModal) {
          setShowLogoutModal(false);
        } else if (isOpen) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, showLogoutModal]);

  // Close on click outside
  const handleBackdropClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleLogoutClick = useCallback(() => {
    setIsOpen(false);
    setShowLogoutModal(true);
  }, []);

  const handleConfirmLogout = useCallback(() => {
    setShowLogoutModal(false);
    onLogout();
  }, [onLogout]);

  return (
    <div className="relative" ref={menuRef}>
      {/* Trigger Button */}
      <button
        onClick={toggleMenu}
        className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="User menu"
      >
        <UserAvatar
          avatarUrl={user?.avatarUrl}
          fullName={user?.fullName}
        />

        <div className="hidden sm:block text-left">
          <p className="text-sm font-medium text-haat-dark">
            {user?.fullName}
          </p>
          <p className="text-xs text-haat-gray-light">
            {user?.email}
          </p>
        </div>

        <ChevronDown
          className={`w-4 h-4 text-haat-gray transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={handleBackdropClick}
              aria-hidden="true"
            />

            {/* Menu */}
            <motion.div
              variants={dropdownVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50"
              role="menu"
              aria-orientation="vertical"
            >
              {/* User Info Section */}
              <div className="p-4 border-b border-gray-100">
                <p className="font-medium text-haat-dark">
                  {user?.fullName}
                </p>
                <p className="text-sm text-haat-gray-light truncate">
                  {user?.email}
                </p>
                {isAdmin && (
                  <span className="inline-block mt-2 px-2 py-0.5 bg-haat-red/10 text-haat-red text-xs font-medium rounded-full">
                    {user?.role === 'super_admin' ? 'Super Admin' : 'Admin'}
                  </span>
                )}
              </div>

              {/* Menu Items */}
              <div className="p-2" role="none">
                {isAdmin && (
                  <a
                    href="/admin"
                    className="flex items-center gap-3 px-3 py-2 text-sm text-haat-dark hover:bg-gray-50 rounded-lg transition-colors"
                    role="menuitem"
                  >
                    <Settings className="w-4 h-4" aria-hidden="true" />
                    Admin Panel
                  </a>
                )}

                <button
                  onClick={handleLogoutClick}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                  role="menuitem"
                >
                  <LogOut className="w-4 h-4" aria-hidden="true" />
                  Sign Out
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      {createPortal(
        <AnimatePresence>
          {showLogoutModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
              onClick={() => setShowLogoutModal(false)}
            >
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              />

              {/* Modal */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                transition={{ type: "spring", damping: 25, stiffness: 300 }}
                onClick={(e) => e.stopPropagation()}
                className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden"
              >
                <div className="px-6 pt-6 pb-4 text-center">
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.1, type: "spring", damping: 15 }}
                    className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-50 to-primary-100 rounded-full flex items-center justify-center"
                  >
                    <LogOut className="w-8 h-8 text-primary-500" />
                  </motion.div>
                  <h3 className="text-lg font-semibold text-gray-900">Sign Out</h3>
                  <p className="mt-2 text-sm text-gray-500">Are you sure you want to sign out?</p>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowLogoutModal(false)}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium text-sm transition-colors"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleConfirmLogout}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-primary-500 to-primary-700 text-white font-medium text-sm shadow-lg shadow-primary-500/25 transition-all hover:shadow-xl"
                  >
                    Yes, Sign Out
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

export default UserDropdownMenu;

